import { PublicationItem } from 'components/discover/publications/model';
import { Keyword, PublicationCategory } from 'components/discover/publications/see-more/model';
import {
  CategoryNode,
  CategoryNodes,
  KeywordNode,
  VideoItem,
} from 'components/discover/publications/video-browser/model';
import { uniqBy } from 'lodash-es';
import { useMemo } from 'react';

function getMatchingPublicationItems(
  items: ReadonlyArray<PublicationItem>,
  activeCategory: number,
  activeKeyword: number | null,
): ReadonlyArray<VideoItem> {
  const matchingItems = items
    .filter(
      ({ categoryId, keywordId }) =>
        (activeCategory === null || categoryId === activeCategory) &&
        (activeKeyword === null || keywordId === activeKeyword),
    )
    .map(
      ({ title, imageUrl, videoId, text, publicationDate }) =>
        ({ title, imageUrl, videoId, text, publicationDate }) as VideoItem,
    );

  return uniqBy(matchingItems, i => i.videoId);
}

export function useCategoryTree(
  categories: readonly PublicationCategory[],
  keywords: readonly Keyword[],
  items: readonly PublicationItem[],
): CategoryNodes {
  const keywordsMap: Record<number, Keyword | undefined> = useMemo(
    () =>
      Object.fromEntries(
        (keywords ?? []).filter(keyword => !keyword.name?.endsWith('Video')).map(keyword => [keyword.id, keyword]),
      ),
    [keywords],
  );

  const keywordsByCategory = useMemo(() => {
    const result: Record<number, Keyword[]> = {};
    for (const item of items) {
      if (item.categoryId) {
        result[item.categoryId] ??= [];
        if (item.keywordId) {
          const keyword = keywordsMap[item.keywordId];
          if (keyword && !result[item.categoryId].includes(keyword)) {
            result[item.categoryId].push(keyword);
          }
        }
      }
    }
    for (const category in result) {
      const orderFunction = (keyword: Keyword) => keywords.findIndex(k => k.id === keyword.id);

      result[category].sort((a, b) => orderFunction(a) - orderFunction(b));
    }
    return result;
  }, [keywordsMap, items, keywords]);

  const tree: CategoryNodes = useMemo(
    () =>
      categories.map(
        c =>
          ({
            id: c.id,
            name: c.name,
            items: getMatchingPublicationItems(items, c.id, null),
            children: keywordsByCategory[c.id].map(
              k =>
                ({
                  id: k.id,
                  name: k.name,
                  items: getMatchingPublicationItems(items, c.id, k.id),
                }) as KeywordNode,
            ),
          }) as CategoryNode,
      ),
    [categories, keywordsByCategory, items],
  );

  return tree;
}
